import { useState, useEffect, useCallback, useRef } from "react";
import { fetchItemPriceLogs, updateItemPriceLogStatus } from "../../service/api/orders";
import "../../css/OrderManagement.css";

const priceDir = (oldP, newP) => {
    const diff = parseFloat(newP) - parseFloat(oldP);
    if (diff > 0) return { arrow: "↑", color: "#ef4444", label: `+${diff.toFixed(2)}` };
    if (diff < 0) return { arrow: "↓", color: "#10b981", label: diff.toFixed(2) };
    return { arrow: "→", color: "#94a3b8", label: "0.00" };
};

const STATUS_OPTIONS = [
    { value: "all",       label: "All" },
    { value: "requested", label: "Requested" },
    { value: "accepted",  label: "Accepted" },
    { value: "rejected",  label: "Rejected" },
];

const STATUS_STYLE = {
    requested: { background: "#fef9c3", color: "#854d0e", border: "1px solid #fde047" },
    accepted:  { background: "#dcfce7", color: "#166534", border: "1px solid #86efac" },
    rejected:  { background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5" },
};

function StatusBadge({ status }) {
    const style = STATUS_STYLE[status] ?? { background: "#f1f5f9", color: "#475569" };
    return (
        <span style={{
            ...style,
            padding: "3px 10px", borderRadius: 20, fontSize: 12,
            fontWeight: 600, textTransform: "capitalize", display: "inline-block",
        }}>
            {status}
        </span>
    );
}

function DetailModal({ log, onClose, onStatusUpdate }) {
    if (!log) return null;
    const dir = priceDir(log.old_price, log.new_price);
    return (
        <div style={S.overlay} onClick={onClose}>
            <div style={S.modal} onClick={(e) => e.stopPropagation()}>
                <div style={S.modalHeader}>
                    <span style={S.modalTitle}>Item Price Change Details</span>
                    <button style={S.closeBtn} onClick={onClose}>✕</button>
                </div>
                <table style={S.detailTable}>
                    <tbody>
                    {[
                        ["Item Name",  log.item_name],
                        ["Type", log.old_price === "0.00" ? "🆕 New Item Request" : "💰 Price Change"],
                        ["Old Price",  `₹${log.old_price}`],
                        ["New Price",  `₹${log.new_price}`],
                        ["Change",     <span style={{ color: dir.color, fontWeight: 700 }}>{dir.arrow} ₹{Math.abs(parseFloat(log.new_price) - parseFloat(log.old_price)).toFixed(2)}</span>],
                        ["Status",     <StatusBadge status={log.status} />],
                        ["Message",    log.message ?? "—"],
                        ["Date",       log.created_at],
                        ["Changed By", log.changed_by],
                    ].map(([label, value]) => (
                        <tr key={label} style={S.detailRow}>
                            <td style={S.detailLabel}>{label}</td>
                            <td style={S.detailValue}>{value}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>

                {log.status === "requested" && (
                    <div style={{ display: "flex", gap: 10, padding: "18px 24px", borderTop: "1px solid #f1f5f9" }}>
                        <button style={S.acceptBtn} onClick={() => onStatusUpdate(log.id, "accepted")}>✓ Accept</button>
                        <button style={S.rejectBtn} onClick={() => onStatusUpdate(log.id, "rejected")}>✕ Reject</button>
                    </div>
                )}
            </div>
        </div>
    );
}

function SkeletonRow() {
    return (
        <tr>
            {[24, 220, 80, 80, 100, 100, 160, 120].map((w, i) => (
                <td key={i} className="om-skeleton-td">
                    <div className="skeleton" style={{ height: 14, width: w, borderRadius: 6 }} />
                </td>
            ))}
        </tr>
    );
}

function StatusDropdown({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const current = STATUS_OPTIONS.find(o => o.value === value) ?? STATUS_OPTIONS[0];

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button style={S.dropdownTrigger} onClick={() => setOpen(o => !o)}>
                {current.label}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                     style={{ marginLeft: 6, transition: "transform .2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
                    <polyline points="6 9 12 15 18 9"/>
                </svg>
            </button>
            {open && (
                <div style={S.dropdownMenu}>
                    {STATUS_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            style={{ ...S.dropdownItem, ...(opt.value === value ? S.dropdownItemActive : {}) }}
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                        >
                            {opt.value !== "all" && (
                                <span style={{
                                    display: "inline-block", width: 8, height: 8, borderRadius: "50%", marginRight: 8,
                                    background: opt.value === "requested" ? "#facc15" : opt.value === "accepted" ? "#22c55e" : "#ef4444",
                                }}/>
                            )}
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── Bulk Action Bar ── */
function BulkActionBar({ count, onAccept, onReject, onClear, busy }) {
    if (count === 0) return null;
    return (
        <div style={S.bulkBar}>
            <span style={S.bulkCount}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: 6 }}>
                    <polyline points="20 6 9 17 4 12"/>
                </svg>
                {count} selected
            </span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                    style={S.bulkAccept}
                    disabled={busy}
                    onClick={onAccept}
                >
                    {busy === "accepted" ? "Processing…" : `✓ Accept All (${count})`}
                </button>
                <button
                    style={S.bulkReject}
                    disabled={busy}
                    onClick={onReject}
                >
                    {busy === "rejected" ? "Processing…" : `✕ Reject All (${count})`}
                </button>
                <button style={S.bulkClear} onClick={onClear} disabled={busy}>
                    Clear
                </button>
            </div>
        </div>
    );
}

export default function ItemPriceLogs({ storeId }) {
    const [logs,        setLogs]        = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState("");
    const [page,        setPage]        = useState(1);
    const [perPage,     setPerPage]     = useState(10);
    const [status,      setStatus]      = useState("all");
    const [meta,        setMeta]        = useState({ total: 0, last_page: 1, from: 0, to: 0 });
    const [selected,    setSelected]    = useState(null);
    const [updating,    setUpdating]    = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkBusy,    setBulkBusy]    = useState(false); // "accepted" | "rejected" | false
    const debounceRef = useRef(null);

    // Only requested-status rows on current page are selectable
    const selectableIds = logs.filter(l => l.status === "requested").map(l => l.id);
    const allSelected   = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));
    const someSelected  = selectableIds.some(id => selectedIds.has(id));

    const load = useCallback(async (q, pg, pp, st) => {
        setLoading(true);
        setSelectedIds(new Set()); // clear selection on reload
        try {
            const params = { store_id: storeId, q, page: pg, per_page: pp };
            if (st !== "all") params.status = st;
            const res = await fetchItemPriceLogs(params);
            setLogs(res.data ?? []);
            setMeta({ total: res.total, last_page: res.last_page, from: res.from, to: res.to });
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [storeId]);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            load(search, 1, perPage, status);
        }, 350);
        return () => clearTimeout(debounceRef.current);
    }, [search, perPage, status]);

    useEffect(() => { load(search, page, perPage, status); }, [page]);

    /* Single accept / reject (also used from modal) */
    const handleStatusUpdate = async (logId, newStatus) => {
        setUpdating(logId);
        try {
            await updateItemPriceLogStatus({ log_id: logId, status: newStatus });
            setLogs(prev => prev.map(l => l.id === logId ? { ...l, status: newStatus } : l));
            if (selected?.id === logId) setSelected(prev => ({ ...prev, status: newStatus }));
            setSelectedIds(prev => { const n = new Set(prev); n.delete(logId); return n; });
        } catch (e) {
            console.error(e);
        } finally {
            setUpdating(null);
        }
    };

    /* Bulk accept / reject */
    const handleBulkUpdate = async (newStatus) => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        setBulkBusy(newStatus);
        try {
            await updateItemPriceLogStatus({ log_ids: ids, status: newStatus });
            // Optimistic update for all affected rows
            setLogs(prev => prev.map(l => ids.includes(l.id) ? { ...l, status: newStatus } : l));
            setSelectedIds(new Set());
        } catch (e) {
            console.error(e);
        } finally {
            setBulkBusy(false);
        }
    };

    /* Checkbox helpers */
    const toggleRow = (id) => {
        setSelectedIds(prev => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    const toggleAll = () => {
        if (allSelected) {
            setSelectedIds(prev => {
                const n = new Set(prev);
                selectableIds.forEach(id => n.delete(id));
                return n;
            });
        } else {
            setSelectedIds(prev => new Set([...prev, ...selectableIds]));
        }
    };

    /* CSV export */
    const exportCSV = () => {
        const header = ["Item Name", "Old Price", "New Price", "Status", "Message", "Changed By", "Date"];
        const rows   = logs.map(l => [l.item_name, l.old_price, l.new_price, l.status, l.message ?? "", l.changed_by, l.created_at]);
        const csv    = [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob   = new Blob([csv], { type: "text/csv" });
        const url    = URL.createObjectURL(blob);
        const a      = document.createElement("a"); a.href = url; a.download = "item_price_logs.csv"; a.click();
        URL.revokeObjectURL(url);
    };

    const buildPages = () => {
        const pages = [];
        const total = meta.last_page;
        if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i); return pages; }
        pages.push(1);
        if (page > 3) pages.push("…");
        for (let i = Math.max(2, page - 1); i <= Math.min(total - 1, page + 1); i++) pages.push(i);
        if (page < total - 2) pages.push("…");
        pages.push(total);
        return pages;
    };

    return (
        <div style={{ margin: 25 }}>
            <style>{`
                @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.45} }
                .skeleton { background: #f1f5f9; animation: pulse 1.4s ease-in-out infinite; }
                .ipl-row:hover { background: #fafbff !important; }
                .ipl-row--selected { background: #f0f7ff !important; }
                .ipl-action-btn { border:none; border-radius:7px; padding:5px 13px; font-size:12px; font-weight:700; cursor:pointer; transition:opacity .15s; }
                .ipl-action-btn:hover { opacity:.82; }
                .ipl-action-btn:disabled { opacity:.45; cursor:not-allowed; }
                .ipl-checkbox { width:16px; height:16px; accent-color:#3b82f6; cursor:pointer; }
                @keyframes slideUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
            `}</style>

            <div className="page-header">
                <div>
                    <h1 className="page-header__title">Item Price Logs</h1>
                </div>
                <div className="page-header__actions">
                    <button className="btn" onClick={exportCSV}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Bulk action bar — floats above table when rows selected */}
            <BulkActionBar
                count={selectedIds.size}
                busy={bulkBusy}
                onAccept={() => handleBulkUpdate("accepted")}
                onReject={() => handleBulkUpdate("rejected")}
                onClear={() => setSelectedIds(new Set())}
            />

            <div className="card" style={{ padding: 0, overflow: "hidden" }}>

                <div className="om-toolbar">
                    <div className="om-search-wrap">
                        <span className="om-search-icon">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                            </svg>
                        </span>
                        <input
                            className="om-search-input"
                            placeholder="Search with anything..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="om-toolbar-filters" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <StatusDropdown value={status} onChange={(v) => { setStatus(v); setPage(1); }} />
                        <select
                            className="om-status-select"
                            value={perPage}
                            onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
                        >
                            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                        </select>
                        <span className="om-count-label">
                            {loading ? "Loading…" : `${meta.total.toLocaleString()} entries`}
                        </span>
                    </div>
                </div>

                <div className="om-table-wrap">
                    <table className="om-table">
                        <thead className="om-thead">
                        <tr>
                            {/* Select-all checkbox — only visible when any selectable rows exist */}
                            <th className="om-th" style={{ width: 40, textAlign: "center" }}>
                                {selectableIds.length > 0 && (
                                    <input
                                        type="checkbox"
                                        className="ipl-checkbox"
                                        checked={allSelected}
                                        ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                        onChange={toggleAll}
                                        title="Select all requested"
                                    />
                                )}
                            </th>
                            <th className="om-th">Item Name</th>
                            <th className="om-th">Type</th>
                            <th className="om-th">Old Price</th>
                            <th className="om-th">New Price</th>
                            <th className="om-th om-hide-md">Change</th>
                            <th className="om-th">Status</th>
                            <th className="om-th">Date</th>
                            <th className="om-th">Changed By</th>
                            <th className="om-th"></th>
                        </tr>
                        </thead>
                        <tbody className="om-tbody">
                        {loading
                            ? Array.from({ length: Math.min(perPage, 10) }).map((_, i) => <SkeletonRow key={i} />)
                            : logs.length === 0
                                ? (
                                    <tr>
                                        <td colSpan={10}>
                                            <div className="om-empty">
                                                <div className="om-empty__icon">📋</div>
                                                <div className="om-empty__title">No price change logs found</div>
                                                <div className="om-empty__sub">Try adjusting your search or filters</div>
                                            </div>
                                        </td>
                                    </tr>
                                )
                                : logs.map(log => {
                                    const dir      = priceDir(log.old_price, log.new_price);
                                    const isBusy   = updating === log.id;
                                    const isChecked = selectedIds.has(log.id);
                                    const canSelect = log.status === "requested";
                                    return (
                                        <tr
                                            key={log.id}
                                            className={`ipl-row${isChecked ? " ipl-row--selected" : ""}`}
                                        >
                                            {/* Row checkbox */}
                                            <td className="om-td" style={{ textAlign: "center", width: 40 }}>
                                                {canSelect && (
                                                    <input
                                                        type="checkbox"
                                                        className="ipl-checkbox"
                                                        checked={isChecked}
                                                        onChange={() => toggleRow(log.id)}
                                                    />
                                                )}
                                            </td>
                                            <td className="om-td">
                                                <span className="om-store-name">{log.item_name}</span>
                                            </td>
                                            <td className="om-td">
                                                {log.old_price === "0.00"
                                                    ? <span style={{ background: "#ede9fe", color: "#5b21b6", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>🆕 New Item</span>
                                                    : <span style={{ background: "#f1f5f9", color: "#475569", padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>💰 Price Change</span>
                                                }
                                            </td>
                                            <td className="om-td">
                                                <span style={{ fontWeight: 600, color: "#64748b" }}>₹{log.old_price}</span>
                                            </td>
                                            <td className="om-td">
                                                <span className="om-total">₹{log.new_price}</span>
                                            </td>
                                            <td className="om-td om-hide-md">
                                                <span style={{ fontWeight: 700, color: dir.color, fontSize: 13 }}>
                                                    {dir.arrow} ₹{Math.abs(parseFloat(log.new_price) - parseFloat(log.old_price)).toFixed(2)}
                                                </span>
                                            </td>
                                            <td className="om-td">
                                                {log.status === "requested" ? (
                                                    <div style={{ display: "flex", gap: 6 }}>
                                                        <button
                                                            className="ipl-action-btn"
                                                            style={{ background: "#dcfce7", color: "#166534" }}
                                                            disabled={isBusy || !!bulkBusy}
                                                            onClick={() => handleStatusUpdate(log.id, "accepted")}
                                                        >
                                                            {isBusy ? "…" : "✓ Accept"}
                                                        </button>
                                                        <button
                                                            className="ipl-action-btn"
                                                            style={{ background: "#fee2e2", color: "#991b1b" }}
                                                            disabled={isBusy || !!bulkBusy}
                                                            onClick={() => handleStatusUpdate(log.id, "rejected")}
                                                        >
                                                            {isBusy ? "…" : "✕ Reject"}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <StatusBadge status={log.status} />
                                                )}
                                            </td>
                                            <td className="om-td">
                                                <span className="om-date">{log.created_at}</span>
                                            </td>
                                            <td className="om-td">
                                                <span style={{ fontSize: 14, color: "#1e293b", fontWeight: 500 }}>{log.changed_by}</span>
                                            </td>
                                            <td className="om-td">
                                                <button className="om-view-btn" onClick={() => setSelected(log)}>
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                        }
                        </tbody>
                    </table>
                </div>

                {!loading && logs.length > 0 && (
                    <div className="om-pagination">
                        <span className="om-pagination__info">
                            Page {page} of {meta.last_page} · {meta.total.toLocaleString()} entries
                        </span>
                        <div className="om-pagination__pages">
                            <button className="om-page-btn om-page-btn--nav" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                            {buildPages().map((p, i) =>
                                p === "…"
                                    ? <span key={`e${i}`} className="om-page-ellipsis">…</span>
                                    : <button key={p} className={`om-page-btn${page === p ? " om-page-btn--active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                            )}
                            <button className="om-page-btn om-page-btn--nav" disabled={page === meta.last_page} onClick={() => setPage(p => p + 1)}>›</button>
                        </div>
                    </div>
                )}
            </div>

            <DetailModal log={selected} onClose={() => setSelected(null)} onStatusUpdate={async (id, st) => { await handleStatusUpdate(id, st); }} />
        </div>
    );
}

/* ─── Inline styles ─────────────────────────────────────────────────────── */
const S = {
    overlay: {
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
        backdropFilter: "blur(2px)",
    },
    modal: {
        background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520,
        margin: 16, boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
    },
    modalHeader: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 24px", borderBottom: "1px solid #f1f5f9",
    },
    modalTitle:  { fontSize: 17, fontWeight: 700, color: "#1e293b", fontFamily: "'Inter', sans-serif" },
    closeBtn: {
        width: 32, height: 32, borderRadius: 8, border: "1px solid #e2e8f0",
        background: "#f8fafc", fontSize: 14, cursor: "pointer", color: "#64748b",
        display: "flex", alignItems: "center", justifyContent: "center",
    },
    detailTable: { width: "100%", borderCollapse: "collapse" },
    detailRow:   { borderBottom: "1px solid #f8fafc" },
    detailLabel: {
        padding: "14px 24px", width: 140, fontSize: 13, fontWeight: 600,
        color: "#64748b", fontFamily: "'Inter', sans-serif",
        background: "#f8fafc", verticalAlign: "top",
    },
    detailValue: {
        padding: "14px 24px", fontSize: 14, color: "#1e293b",
        fontFamily: "'Inter', sans-serif", wordBreak: "break-word",
    },
    acceptBtn: {
        flex: 1, padding: "9px 0", borderRadius: 8, border: "none",
        background: "#16a34a", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
    },
    rejectBtn: {
        flex: 1, padding: "9px 0", borderRadius: 8, border: "none",
        background: "#dc2626", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer",
    },
    dropdownTrigger: {
        display: "flex", alignItems: "center", padding: "7px 12px",
        border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff",
        fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer", whiteSpace: "nowrap",
    },
    dropdownMenu: {
        position: "absolute", top: "calc(100% + 6px)", right: 0,
        background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 50,
        minWidth: 150, padding: "4px 0", overflow: "hidden",
    },
    dropdownItem: {
        display: "flex", alignItems: "center", width: "100%",
        padding: "9px 16px", border: "none", background: "transparent",
        fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer", textAlign: "left",
    },
    dropdownItemActive: { background: "#f1f5f9", color: "#1e293b", fontWeight: 700 },

    /* Bulk bar */
    bulkBar: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "#1e293b", color: "#fff", borderRadius: 12,
        padding: "10px 18px", marginBottom: 12,
        animation: "slideUp .2s ease",
        boxShadow: "0 4px 20px rgba(30,41,59,0.25)",
    },
    bulkCount: { display: "flex", alignItems: "center", fontSize: 14, fontWeight: 600 },
    bulkAccept: {
        padding: "7px 18px", borderRadius: 8, border: "none",
        background: "#22c55e", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
    },
    bulkReject: {
        padding: "7px 18px", borderRadius: 8, border: "none",
        background: "#ef4444", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
    },
    bulkClear: {
        padding: "7px 14px", borderRadius: 8,
        border: "1px solid rgba(255,255,255,0.25)", background: "transparent",
        color: "#cbd5e1", fontWeight: 600, fontSize: 13, cursor: "pointer",
    },
};